import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import ShieldIcon from '@atlaskit/icon/core/shield';
import Skeleton from '@atlaskit/skeleton';

import {
	ContentClassificationTag,
	ContentTypeClassification,
	FlagStatus,
} from '@confluence/data-classification';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { ContentType } from '@confluence/content-types-utils';
import { fg } from '@confluence/feature-gating';

import { useContentClassification } from '../hooks/useContentClassification';

import { DetailRow } from './DetailRow';
import { InlineRetryError } from './InlineRetryError';
import { ContentClassifier } from './ContentClassifier';

const i18n = defineMessages({
	classificationSection: {
		id: 'details-panel.activity-section.classification-row',
		defaultMessage: 'Classification',
		description:
			"A title that appears next to the content's classification (highly confidential, confidential, internal, public)",
	},
});

export const ClassificationRow = () => {
	const { formatMessage } = useIntl();
	const [contentType] = useContentType();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const pageMode = useGetPageMode();
	const { data, error, loading, refetch } = useContentClassification();

	const shouldRenderContentClassificationTag =
		contentType === ContentType.PAGE &&
		(pageMode === PageMode.VIEW || data.flagStatus === FlagStatus.LOADING);

	const renderClassificationItem = () => {
		if (loading) {
			return (
				<Skeleton width="80px" height="24px" borderRadius={3} testId="classification-row-loading" />
			);
		}

		if (error) {
			return <InlineRetryError error={error} refetch={refetch} />;
		}

		return shouldRenderContentClassificationTag ? (
			<>
				{fg('confluence_classification_row_content') ? (
					<ContentTypeClassification
						contentId={contentId ?? ''}
						contentType={contentType ?? ''}
						contentClassificationMetadata={data}
						spaceKey={spaceKey ?? ''}
						readOnly
					/>
				) : (
					<ContentClassificationTag
						contentId={contentId ?? ''}
						contentType={contentType ?? ''}
						contentClassificationMetadata={data}
					/>
				)}
			</>
		) : (
			<ContentClassifier contentClassificationMetadata={data} />
		);
	};

	return (
		<DetailRow itemName={formatMessage(i18n.classificationSection)} icon={<ShieldIcon label="" />}>
			{renderClassificationItem()}
		</DetailRow>
	);
};
